import React, { useRef, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FilterOption } from "./SustainTeamTypes";
import { font } from "../SustainTeamManagementScreen.styles";

interface Props {
  /** Button label when no items are selected (e.g. "All Teams"). */
  defaultLabel: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const PANEL_WIDTH = 220;

/**
 * Standalone multi-select dropdown for the Calendar View filter bar.
 * Shows "defaultLabel" when nothing is selected, otherwise shows a count badge.
 */
const CalendarFilterDropdown: React.FC<Props> = ({
  defaultLabel,
  options,
  selected,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<View>(null);
  const active = selected.length > 0;

  const toggleValue = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const handleTriggerPress = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const node = triggerRef.current as any;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, _w: number, height: number) => {
        setPanelPos({ top: y + height + 4, left: x });
        setOpen(true);
      });
    } else {
      setOpen(true);
    }
  };

  const displayLabel = active
    ? `${defaultLabel.replace(/^All /, "")} (${selected.length})`
    : defaultLabel;

  return (
    <View ref={triggerRef} collapsable={false} style={cfd.wrap}>
      <Pressable
        style={({ pressed }) => [cfd.trigger, active && cfd.triggerActive, pressed && cfd.triggerPressed]}
        onPress={handleTriggerPress}
      >
        <Text style={[cfd.triggerText, active && cfd.triggerTextActive]} numberOfLines={1}>
          {displayLabel}
        </Text>
        <MaterialIcons
          name={open ? "expand-less" : "expand-more"}
          size={18}
          color={active ? "#034895" : "#6b7280"}
        />
      </Pressable>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <View style={{ flex: 1 }}>
          <Pressable style={StyleSheet.absoluteFillObject} onPress={() => setOpen(false)} />
          {panelPos && (
            <View style={[cfd.panel, { top: panelPos.top, left: panelPos.left }]}>
              <ScrollView style={cfd.optionList} nestedScrollEnabled>
                {options.length === 0 ? (
                  <Text style={cfd.emptyText}>No options</Text>
                ) : (
                  options.map((opt) => {
                    const checked = selected.includes(opt.value);
                    return (
                      <Pressable
                        key={opt.value}
                        style={cfd.option}
                        onPress={() => toggleValue(opt.value)}
                      >
                        <View style={[cfd.checkbox, checked && cfd.checkboxChecked]}>
                          {checked && <MaterialIcons name="check" size={10} color="#fff" />}
                        </View>
                        <Text style={cfd.optionText} numberOfLines={1}>
                          {opt.label}
                        </Text>
                      </Pressable>
                    );
                  })
                )}
              </ScrollView>
              {active && (
                <Pressable style={cfd.clearBtn} onPress={() => onChange([])}>
                  <Text style={cfd.clearBtnText}>Clear filter</Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

export default CalendarFilterDropdown;

const cfd = StyleSheet.create({
  wrap: { flexDirection: "row", alignItems: "center" },

  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#fff",
    minWidth: 140,
    ...({ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" } as any),
  } as any,

  triggerActive: {
    borderColor: "#93BBF0",
    backgroundColor: "#EEF5FF",
  },

  triggerPressed: {
    backgroundColor: "#E5EEFA",
  },

  triggerText: {
    flex: 1,
    fontSize: 13,
    color: "#374151",
    ...font("500"),
  } as any,

  triggerTextActive: {
    color: "#034895",
  },

  panel: {
    position: "absolute",
    width: PANEL_WIDTH,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#E4E9F2",
    borderRadius: 8,
    paddingVertical: 6,
    zIndex: 9999,
    ...({ boxShadow: "0 8px 24px rgba(0,20,60,0.14)" } as any),
  } as any,

  optionList: { maxHeight: 220 },

  emptyText: {
    fontSize: 12,
    color: "#9a9a9a",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },

  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },

  optionText: {
    fontSize: 13,
    color: "#1f2937",
    flex: 1,
    ...font("400"),
  } as any,

  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },

  checkboxChecked: {
    backgroundColor: "#034895",
    borderColor: "#034895",
  },

  clearBtn: {
    borderTopWidth: 1,
    borderTopColor: "#F0F0F0",
    marginTop: 4,
    paddingTop: 7,
    paddingBottom: 4,
    paddingHorizontal: 10,
  },

  clearBtnText: {
    fontSize: 12,
    color: "#034895",
    ...font("600"),
  } as any,
});
